import type { RunMessageMention } from '@trevize/db'

/**
 * Mention resolution for collaborative-session messages.
 *
 * Kept free of `db`/AI imports so the Zero client mutator can run the exact
 * same logic optimistically — the optimistic `run_messages` row has to carry
 * the same mentions the server will persist, otherwise the "N queued" badge
 * flickers when the authoritative row syncs back.
 */

/** A literal `@agent`, at the start of the message or after whitespace. */
const AGENT_MENTION_PATTERN = /(^|\s)@agent\b/i

/**
 * Resolve the mentions for a message: the client passes any resolved `@user`
 * mentions (it knows the roster), and we additionally detect a literal `@agent`
 * in the text so addressing the agent is always recorded — mentioning the agent
 * is how a message is "directed at the agent". Duplicates are removed.
 */
export function resolveMentions(
  text: string,
  clientMentions: RunMessageMention[] = [],
): RunMessageMention[] {
  const mentions = [...clientMentions]
  if (
    AGENT_MENTION_PATTERN.test(text) &&
    !mentions.some((mention) => mention.type === 'agent')
  ) {
    mentions.push({ type: 'agent' })
  }

  const seen = new Set<string>()
  return mentions.filter((mention) => {
    const key = mention.type === 'agent' ? 'agent' : `user:${mention.userId}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}
